import { useSelection } from "@/contexts/SelectionContext";
import { useFragment } from "@/generated";
import {
  LessonFieldsFragmentDoc,
  type LessonInput,
  ToggleLessonIsHiddenDocument,
  UpdateLessonDocument,
} from "@/generated/graphql";
import { hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { withToast } from "@/lib/utils/withToast";
import { ErrorCodes } from "@ai-teach/types";
import { useMutation } from "@apollo/client/react";

export function useEditLesson() {
  const { toggleUnlinkModalOpen } = useSelection();

  // Apollo handle entity cache update
  const [updateLessonMutation, { loading: updateLessonLoading }] =
    useMutation(UpdateLessonDocument);

  const editLesson = async (
    updateLessonId: string,
    input: LessonInput,
    thumbnailFile?: File | null,
    removeImage?: boolean
  ) => {
    try {
      return await withToast(
        async () => {
          const result = await updateLessonMutation({
            variables: {
              updateLessonId,
              input,
              thumbnailFile: thumbnailFile || undefined,
              removeImage,
            },
          });

          const maskedLesson = result.data?.updateLesson;
          const updatedLesson = maskedLesson
            ? useFragment(LessonFieldsFragmentDoc, maskedLesson)
            : undefined;

          return updatedLesson;
        },
        {
          loading: "Updating lesson...",
          success: "Lesson updated successfully!",
          error: "Failed to update lesson. Please try again.",
        },
        "editLesson"
      );
    } catch (error) {
      if (hasGraphQLErrorCode(error, ErrorCodes.CANNOT_UPDATE_LINKED_COPY)) {
        toggleUnlinkModalOpen({ type: "Lesson", itemId: updateLessonId });
      }
      return;
    }
  };

  const [toggleLessonIsHiddenMutation, { loading: toggleIsHiddenLoading }] = useMutation(
    ToggleLessonIsHiddenDocument
  );

  const toggleLessonIsHidden = async (lessonId: string, isHidden: boolean) => {
    try {
      const result = await toggleLessonIsHiddenMutation({
        variables: {
          lessonId,
          isHidden,
        },
      });

      const maskedLesson = result.data?.toggleLessonIsHidden;
      const toggledLesson = maskedLesson
        ? useFragment(LessonFieldsFragmentDoc, maskedLesson)
        : undefined;

      return toggledLesson;
    } catch (error) {
      if (hasGraphQLErrorCode(error, ErrorCodes.CANNOT_UPDATE_LINKED_COPY)) {
        toggleUnlinkModalOpen({ type: "Lesson", itemId: lessonId });
      }
      return;
    }
  };

  return {
    editLesson,
    toggleLessonIsHidden,
    loading: toggleIsHiddenLoading || updateLessonLoading,
  };
}
